/**
 * Minimal fetch wrapper for the TradeLogic backend (vendor portal).
 *
 * - Base URL configurable via VITE_API_BASE_URL (default /api).
 *   In dev the Vite proxy rewrites /api -> /v1 so the request reaches
 *   the NestJS controllers mounted at /v1/* (see vite.config.ts).
 * - Auto-attaches Bearer access token from authBridge when no explicit
 *   token is supplied.
 * - On HTTP 401 the wrapper asks authBridge to refresh the access token
 *   exactly once and retries the original request. If refresh fails or
 *   no refresh handler is registered, the original 401 ApiError is
 *   thrown so the caller (or AuthProvider) can clear state. (INT-012)
 * - Throws ApiError on non-2xx; callers map to UX.
 */

import { getAccessToken, triggerRefresh } from '../auth/authBridge.js';

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiRequestInit {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly token?: string | null;
  readonly headers?: Record<string, string>;
  /** Internal: skip the 401-refresh leg (used by the retry to avoid loops). */
  readonly _retried?: boolean;
}

const baseUrl: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL) ||
  '/api';

/* ── Active tenant ──────────────────────────────────────────────────── */

const TENANT_STORAGE_KEY = 'tradelogic.vendor.tenant.v1';

function envTenantCode(): string {
  return (import.meta as { env?: { VITE_TENANT_CODE?: string } }).env?.VITE_TENANT_CODE?.trim() ?? '';
}

function readStoredTenantCode(): string {
  if (typeof window === 'undefined' || !window.localStorage) return '';
  try {
    return window.localStorage.getItem(TENANT_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

let activeTenantCode: string = readStoredTenantCode();

/**
 * The tenant chosen on the sign-in screen. `/v1/auth/login` rejects a request
 * without X-Tenant-Code (EX-TEN-05), so this must be set before signing in.
 * Persisted because refresh/logout run long after the form is gone.
 */
export function setTenantCode(code: string): void {
  activeTenantCode = code.trim();
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (activeTenantCode) window.localStorage.setItem(TENANT_STORAGE_KEY, activeTenantCode);
    else window.localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch { /* ignore quota / disabled storage */ }
}

export function getTenantCode(): string {
  return activeTenantCode || envTenantCode();
}

function tenantHeaders(): Record<string, string> {
  const tenantCode = getTenantCode();
  return tenantCode ? { 'X-Tenant-Code': tenantCode } : {};
}

async function doFetch(path: string, init: ApiRequestInit, tokenOverride: string | null | undefined): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...tenantHeaders(),
    ...(init.headers ?? {}),
  };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  const token = tokenOverride === undefined ? getAccessToken() : tokenOverride;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(`${baseUrl}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    // SEC-001: send httpOnly `refresh_token` cookie on /auth/refresh.
    credentials: 'include',
  });
}

async function parseResponse<TResponse>(res: Response): Promise<TResponse> {
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    // GlobalExceptionFilter answers with { code, user_message }; only a few
    // routes return Nest's default { message }. Reading just `message` showed
    // every filtered error as a bare "HTTP 400".
    const obj = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    const detail =
      obj && typeof obj.user_message === 'string'
        ? obj.user_message
        : obj && 'message' in obj
          ? String(obj.message)
          : null;
    const code = obj && typeof obj.code === 'string' ? obj.code : null;
    const msg = detail ? (code ? `${detail} (${code})` : detail) : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, msg);
  }
  return parsed as TResponse;
}

export async function apiRequest<TResponse>(path: string, init: ApiRequestInit = {}): Promise<TResponse> {
  const isAuthEndpoint = path.startsWith('/auth/');
  const tokenOverride = 'token' in init ? init.token : undefined;
  let res = await doFetch(path, init, tokenOverride);

  if (res.status === 401 && !init._retried && !isAuthEndpoint) {
    const newToken = await triggerRefresh();
    if (newToken) {
      res = await doFetch(path, { ...init, _retried: true }, newToken);
    }
  }

  return parseResponse<TResponse>(res);
}
